import {QUERIES} from '@github-ui/issue-url-helper/known-queries'
import {retainOpenClosedCounts} from '@github-ui/issues-tab-counter/open-closed-count'
import {ListViewSectionFilterLink} from '@github-ui/list-view/ListViewSectionFilterLink'
import type {ScopedRepository} from '@github-ui/list-view-items-issues-prs/Query'
import {checkIfStateReasonPresent, getQuery} from '@github-ui/list-view-items-issues-prs/Query'
/* eslint-disable-next-line @github-ui/github-monorepo/no-use-app-payload --
   useAppPayload is deprecated. See
   https://thehub.github.com/epd/engineering/dev-practicals/frontend/react/data-router/migration/app-payload-deprecation/ */
import {useAppPayload} from '@github-ui/react-core/use-app-payload'
import {useLocation} from '@github-ui/react-router'
import {useRuntimeEnvironment} from '@github-ui/ssr-utils/use-runtime-environment'
import {testIdProps} from '@github-ui/test-id-props'
import {useIsPlatform} from '@github-ui/use-is-platform'
import {clsx} from 'clsx'
import {Suspense, useCallback, useEffect, useMemo} from 'react'
import {graphql, useLazyLoadQuery, useRelayEnvironment} from 'react-relay'

import {useActiveSearchQuery} from '../../../contexts/QueryContext'
import type {AppPayload} from '../../../types/app-payload'
import type {IssueSearchType} from '../../../types/issue-search-type'
import {kFormatter} from '../../../utils/descriptions'
import {
  getClosedHref,
  getOpenHref,
  isClosedQuery,
  isNegatedClosedQuery,
  isNegatedOpenQuery,
  isOpenQuery,
} from '../../../utils/urls'
import styles from '../ListItems.module.css'
import type {OpenClosedTabsQuery} from './__generated__/OpenClosedTabsQuery.graphql'

type OpenClosedProps = {
  applySectionFilter?: (href: string, url: string) => void
  scopedRepository?: ScopedRepository
  searchType?: IssueSearchType
}

const SuspendedFilterLinks = ({
  isClosedTabActive,
  isOpenTabActive,
}: {
  isClosedTabActive: boolean
  isOpenTabActive: boolean
}) => (
  <div className={`${clsx(styles.tabsContainer, styles.loading)}`}>
    <ListViewSectionFilterLink key="open" title="Open" isSelected={isOpenTabActive} isLoading href={''} />
    <ListViewSectionFilterLink key="closed" title="Closed" isSelected={isClosedTabActive} isLoading href={''} />
  </div>
)

export function OpenClosedTabs(props: OpenClosedProps) {
  const activeSearchQuery = useActiveSearchQuery()
  const {isBrowser} = useRuntimeEnvironment()

  const isOpen = isOpenQuery(activeSearchQuery)
  const isClosed = isClosedQuery(activeSearchQuery)
  const isNegatedOpen = isNegatedOpenQuery(activeSearchQuery)
  const isNegatedClosed = isNegatedClosedQuery(activeSearchQuery)

  const isOpenTabActive = (isOpen && !isNegatedOpen) || isNegatedClosed
  const isClosedTabActive = (isClosed && !isNegatedClosed) || isNegatedOpen

  const fallback = <SuspendedFilterLinks isOpenTabActive={isOpenTabActive} isClosedTabActive={isClosedTabActive} />

  // Render the fallback directly during SSR to avoid hitting a Suspense boundary
  // that renderToString cannot handle, which would abort SSR for the entire element.
  if (!isBrowser) {
    return fallback
  }

  return (
    <Suspense fallback={fallback}>
      <OpenClosedTabsInternal {...props} />
    </Suspense>
  )
}

function OpenClosedTabsInternal({applySectionFilter, scopedRepository, searchType}: OpenClosedProps) {
  const activeSearchQuery = useActiveSearchQuery()
  const environment = useRelayEnvironment()
  const {best_match_sort_enabled} = useAppPayload<AppPayload>()
  // Use React Router's reactive `useLocation()` instead of the module-level
  // `ssrSafeLocation` so the React Compiler tracks the pathname as a reactive
  // dependency. Reading from the SSR-safe module wrapper would let the compiler
  // freeze `urlPath` (and the `useCallback` / derived hrefs below) at the first
  // render's pathname, producing stale tab links after navigation.
  //
  // Collapse leading slashes to prevent protocol-relative open redirect when
  // hydrating on URLs like `//attacker/...`
  const {pathname} = useLocation()
  const urlPath = pathname.replace(/^\/{2,}/, '/')

  const openHref = useMemo(() => getOpenHref(activeSearchQuery), [activeSearchQuery])
  const openQueryStr = openHref ? `?q=${encodeURIComponent(openHref)}` : ''
  const openHrefUrl = `${urlPath}${openQueryStr}`

  const closedHref = useMemo(() => getClosedHref(activeSearchQuery), [activeSearchQuery])
  const closedHrefUrl = closedHref ? `${urlPath}?q=${encodeURIComponent(closedHref)}` : urlPath
  const isMac = useIsPlatform(['mac'])
  const onClick = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>, isOpen: boolean) => {
      if (!activeSearchQuery || !applySectionFilter) return

      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      const isMetaKey = isMac ? event.metaKey : event.ctrlKey
      if (isMetaKey) {
        return
      }

      event.preventDefault()
      // Use the pre-computed relative URLs instead of event.currentTarget.href (absolute URL).
      // Absolute URLs cause useNavigate to treat the destination as external, triggering a
      // hard (Turbo) navigation instead of client-side React Router navigation.
      const url = isOpen ? openHrefUrl : closedHrefUrl
      applySectionFilter(isOpen ? openHref || QUERIES.defaultRepoLevelOpen : closedHref, url)
    },
    [activeSearchQuery, applySectionFilter, closedHref, closedHrefUrl, isMac, openHref, openHrefUrl],
  )

  const isOpen = isOpenQuery(activeSearchQuery)
  const isClosed = isClosedQuery(activeSearchQuery)
  const isNegatedOpen = isNegatedOpenQuery(activeSearchQuery)
  const isNegatedClosed = isNegatedClosedQuery(activeSearchQuery)

  const isOpenTabActive = (isOpen && !isNegatedOpen) || isNegatedClosed
  const isClosedTabActive = (isClosed && !isNegatedClosed) || isNegatedOpen

  const query = useMemo(() => {
    let searchQuery = checkIfStateReasonPresent(openHref) ? closedHref : openHref
    searchQuery = getQuery(searchQuery, scopedRepository, best_match_sort_enabled)

    if (openHref) return searchQuery

    // When there is no openHref (when the page is loaded for the first time), we need to add is:issue to the query
    // to make sure we only count issues
    return `is:issue ${searchQuery}`
  }, [closedHref, openHref, scopedRepository, best_match_sort_enabled])

  // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
  const queryNode = graphql`
    query OpenClosedTabsQuery(
      $query: String = "archived:false assignee:@me sort:updated-desc"
      $name: String!
      $owner: String!
      $type: SearchType = ISSUE_ADVANCED
    ) {
      repository(owner: $owner, name: $name) {
        id
        search(first: 0, query: $query, type: $type, aggregations: true) {
          closedIssueCount
          openIssueCount
        }
      }
    }
  `

  const issueSearchType = searchType ?? 'ISSUE_ADVANCED'

  const variables = useMemo(
    () => ({
      query,
      owner: scopedRepository!.owner,
      name: scopedRepository!.name,
      type: issueSearchType,
    }),
    [query, scopedRepository, issueSearchType],
  )

  const data = useLazyLoadQuery<OpenClosedTabsQuery>(queryNode, variables, {fetchPolicy: 'store-or-network'})

  // Retain the query operation so the search record survives navigation to issue#show.
  // Allows the close/reopen mutation's optimisticUpdater to find and adjust the counts.
  useEffect(() => {
    if (!data.repository?.id) return

    const searchQueryArgs = {
      first: 0,
      query,
      type: issueSearchType,
      aggregations: true,
    }
    retainOpenClosedCounts({
      environment,
      queryNode,
      variables,
      repoDataId: data.repository.id,
      queryArgs: searchQueryArgs,
    })
  }, [environment, queryNode, variables, data.repository?.id, query, issueSearchType])

  const closedIssueCount = data.repository?.search?.closedIssueCount || 0
  const openIssueCount = data.repository?.search?.openIssueCount || 0

  return (
    <div {...testIdProps('list-view-section-filters')}>
      <ul className={`list-style-none ${clsx(styles.tabsContainer)}`}>
        <li key={`section-filter-0`} {...testIdProps(`list-view-section-filter-0`)}>
          <ListViewSectionFilterLink
            key="open"
            title="Open"
            isSelected={isOpenTabActive && !isClosedTabActive}
            count={kFormatter(openIssueCount)}
            href={openHrefUrl}
            onClick={e => onClick(e, true)}
          />
        </li>
        <li key={`section-filter-1`} {...testIdProps(`list-view-section-filter-1`)}>
          <ListViewSectionFilterLink
            key="closed"
            title="Closed"
            isSelected={isClosedTabActive && !isOpenTabActive}
            count={kFormatter(closedIssueCount)}
            href={closedHrefUrl}
            onClick={e => onClick(e, false)}
          />
        </li>
      </ul>
    </div>
  )
}
